import * as React from "react";
import { Button, cn } from "@finster/ui";
import { Loader2 } from "lucide-react";
import type { SkillDecision, SkillDecisionSkill } from "../types";

export interface SkillDecisionBannerProps {
  decision: SkillDecision;
  onViewSkill: (skill: SkillDecisionSkill) => void;
  onRunWithSkills: (skills: SkillDecisionSkill[]) => void;
  onRunWithoutSkills: () => void;
  onDismiss: () => void;
  stoppingRun?: boolean;
  runError?: string;
  className?: string;
}

const ACTION_BUTTON_CLASS =
  "h-7 min-w-0 max-w-full cursor-pointer gap-1 rounded-md px-2 text-[11px]";

const RUN_BUTTON_CLASS = `${ACTION_BUTTON_CLASS} font-semibold`;

const SLUG_LINK_CLASS =
  "inline cursor-pointer rounded-sm border-0 bg-transparent p-0 align-baseline text-[length:inherit] font-normal leading-[inherit] text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus disabled:cursor-default disabled:opacity-50";

function skillSlashLabel(skill: SkillDecisionSkill): string {
  return `/${skill.slug}`;
}

function SkillSlugLink({
  skill,
  onView,
  disabled,
}: {
  skill: SkillDecisionSkill;
  onView: (skill: SkillDecisionSkill) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={SLUG_LINK_CLASS}
      onClick={() => onView(skill)}
    >
      <span className="whitespace-nowrap" translate="no">
        {skillSlashLabel(skill)}
      </span>
    </button>
  );
}

function SkillSlashTokens({
  skills,
  onViewSkill,
  disabled,
}: {
  skills: readonly SkillDecisionSkill[];
  onViewSkill: (skill: SkillDecisionSkill) => void;
  disabled?: boolean;
}) {
  return skills.map((skill, index) => (
    <React.Fragment key={skill.id}>
      {index > 0 ? ", " : null}
      <SkillSlugLink skill={skill} onView={onViewSkill} disabled={disabled} />
    </React.Fragment>
  ));
}

/**
 * Compact matching-skill strip above the composer. Inspect is every /slug link.
 */
export const SkillDecisionBanner: React.FC<SkillDecisionBannerProps> = ({
  decision,
  onViewSkill,
  onRunWithSkills,
  onRunWithoutSkills,
  onDismiss,
  stoppingRun = false,
  runError,
  className,
}) => {
  const errorRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (runError) errorRef.current?.focus();
  }, [runError]);

  if (decision.skills.length === 0) return null;

  const usingMatchingSkill = decision.kind === "usingMatchingSkill";
  const hasMultipleSkills = decision.skills.length > 1;
  const skillTokens = (
    <SkillSlashTokens skills={decision.skills} onViewSkill={onViewSkill} disabled={stoppingRun} />
  );
  const runLabel = stoppingRun
    ? "Stopping previous run…"
    : usingMatchingSkill
      ? "Run without"
      : hasMultipleSkills
        ? "Run with skills"
        : "Run with skill";
  const title = usingMatchingSkill
    ? `Using matching skill${hasMultipleSkills ? "s" : ""}`
    : "Recommended";

  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-0.5 rounded-lg border border-primary/15 bg-primary/[0.04] px-2.5 py-1.5 text-[11px] leading-4",
        className
      )}
      aria-busy={stoppingRun || undefined}
    >
      <div className="flex min-w-0 items-center gap-2">
        <p className="m-0 min-w-0 flex-1 truncate text-xs font-semibold leading-[18px] text-foreground">
          {title}
        </p>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="mini"
            disabled={stoppingRun}
            className={cn(
              ACTION_BUTTON_CLASS,
              "text-muted-foreground hover:bg-primary/10 hover:text-foreground"
            )}
            onClick={onDismiss}
          >
            Dismiss
          </Button>
          <Button
            type="button"
            variant={stoppingRun ? "ghost" : usingMatchingSkill ? "outline" : undefined}
            size="mini"
            disabled={stoppingRun}
            className={cn(
              stoppingRun ? ACTION_BUTTON_CLASS : RUN_BUTTON_CLASS,
              stoppingRun
                ? "text-muted-foreground [&_svg]:size-3.5"
                : usingMatchingSkill
                  ? "border-primary/15"
                  : undefined
            )}
            onClick={() =>
              usingMatchingSkill ? onRunWithoutSkills() : onRunWithSkills(decision.skills)
            }
          >
            {stoppingRun ? (
              <>
                <Loader2 className="size-3.5 animate-spin motion-reduce:animate-none" aria-hidden />
                {runLabel}
              </>
            ) : (
              runLabel
            )}
          </Button>
        </div>
      </div>
      <div role="status">
        <p className="m-0 break-words text-muted-foreground">
          {usingMatchingSkill ? (
            <>We&apos;re using {skillTokens}.</>
          ) : (
            <>We recommend {skillTokens}.</>
          )}
        </p>
      </div>
      {runError ? (
        <div
          ref={errorRef}
          role="alert"
          tabIndex={-1}
          className="outline-none focus-visible:ring-2 focus-visible:ring-ring-focus"
        >
          <p className="m-0 text-[11px] leading-4 text-destructive">{runError}</p>
        </div>
      ) : null}
    </div>
  );
};
